import type { XdaSettingsState, XdaSettingsStore } from "./xdaSettingsStore";

// NOTE: `selectXdaSettings` returns a new object literal every call and MUST
// be consumed with `useShallow`:
//   import { useShallow } from "zustand/react/shallow";
//   useXdaSettingsStore(useShallow(selectXdaSettings))

// ── Scalar selectors ─────────────────────────────────────────────────────────
export const selectActiveXdaPlay = (s: XdaSettingsStore) => s.activeXdaPlay;
export const selectBin = (s: XdaSettingsStore) => s.bin;
export const selectCompletionLength = (s: XdaSettingsStore) => s.completionLength;
export const selectDataFields = (s: XdaSettingsStore) => s.dataFields;
export const selectDownHeight = (s: XdaSettingsStore) => s.downHeight;
export const selectHangWellsToTop = (s: XdaSettingsStore) => s.hangWellsToTop;
export const selectIsUsingResourcePlayDefault = (s: XdaSettingsStore) =>
  s.isUsingResourcePlayDefault;
export const selectIpdbColor = (s: XdaSettingsStore) => s.ipdbColor;
export const selectIpdbUnit = (s: XdaSettingsStore) => s.ipdbUnit;
export const selectIpdbTitleSize = (s: XdaSettingsStore) => s.ipdbTitleSize;
export const selectIpdbFontSize = (s: XdaSettingsStore) => s.ipdbFontSize;
export const selectIpdbOpacity = (s: XdaSettingsStore) => s.ipdbOpacity;
export const selectLeftWidth = (s: XdaSettingsStore) => s.leftWidth;
export const selectReverseColor = (s: XdaSettingsStore) => s.reverseColor;
export const selectRightWidth = (s: XdaSettingsStore) => s.rightWidth;
export const selectScaleByOption = (s: XdaSettingsStore) => s.scaleByOption;
export const selectScaleByValue = (s: XdaSettingsStore) => s.scaleByValue;
export const selectShowCompletion = (s: XdaSettingsStore) => s.showCompletion;
export const selectShowData = (s: XdaSettingsStore) => s.showData;
export const selectShowGrid = (s: XdaSettingsStore) => s.showGrid;
export const selectShowIpdb = (s: XdaSettingsStore) => s.showIpdb;
export const selectShowIpdbLegend = (s: XdaSettingsStore) => s.showIpdbLegend;
export const selectShowMeasurement = (s: XdaSettingsStore) => s.showMeasurement;
export const selectShowTooltip = (s: XdaSettingsStore) => s.showTooltip;
export const selectShowXdaValueInfo = (s: XdaSettingsStore) => s.showXdaValueInfo;
export const selectShowRelativeDepth = (s: XdaSettingsStore) => s.showRelativeDepth;
export const selectShowTVDSSDepth = (s: XdaSettingsStore) => s.showTVDSSDepth;
export const selectUpHeight = (s: XdaSettingsStore) => s.upHeight;
export const selectTopsModelSource = (s: XdaSettingsStore) => s.topsModelSource;
export const selectShowAllTops = (s: XdaSettingsStore) => s.showAllTops;
export const selectShowOverlap = (s: XdaSettingsStore) => s.showOverlap;
export const selectShowDucWells = (s: XdaSettingsStore) => s.showDucWells;
export const selectSelectedRadius = (s: XdaSettingsStore) => s.selectedRadius;
export const selectWidthScaled = (s: XdaSettingsStore) => s.widthScaled;
export const selectResetZuiInstance = (s: XdaSettingsStore) => s.resetZuiInstance;
export const selectIncludeZero = (s: XdaSettingsStore) => s.includeZero;
export const selectSpacingClassFilter = (s: XdaSettingsStore) => s.spacingClassFilter;
export const selectOctantLengthOverrides = (s: XdaSettingsStore) =>
  s.octantLengthOverrides;
export const selectOctantHorCutoffDeg = (s: XdaSettingsStore) => s.octantHorCutoffDeg;
export const selectOctantVertCutoffDeg = (s: XdaSettingsStore) => s.octantVertCutoffDeg;

export const selectUpdateXdaSettings = (s: XdaSettingsStore) => s.updateXdaSettings;
export const selectApplyRadiusPreset = (s: XdaSettingsStore) => s.applyRadiusPreset;
export const selectReset = (s: XdaSettingsStore) => s.reset;

// ── Compound selectors ───────────────────────────────────────────────────────

/**
 * Full flat settings object (actions stripped) — for call sites that read many
 * fields at once and/or need to spread "current settings + overrides" before
 * calling `setXdaSettings` (the full-replace call-site contract every write
 * site already follows). Returns a new object on every call — consumers MUST
 * use shallow equality:
 *
 *   import { useShallow } from "zustand/react/shallow";
 *   const settings = useXdaSettingsStore(useShallow(selectXdaSettings));
 */
export const selectXdaSettings = (s: XdaSettingsStore): XdaSettingsState => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { setXdaSettings, updateXdaSettings, reset, applyRadiusPreset, ...settings } = s;
  return settings;
};
